import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, ImageReveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { IMAGES, COLLECTIONS, CAPABILITIES, SITE } from "../lib/site";

const ease = [0.16, 1, 0.3, 1];

export default function Home() {
    return (
        <div data-testid="page-home">
            <Seo
                title="Luxury Linen Manufacturer & Exporter"
                description="Vyom — luxury bed linen manufacturer in India. Premium bedsheets, blankets, duvet covers, pillow covers, quilts, table linen and bath towels in 100% cotton, 100% linen and blends. White-label and hospitality linen, trusted by distinguished brands across the world."
                path="/"
                image="https://customer-assets.emergentagent.com/job_minimal-luxury-47/artifacts/8ieeet28_vyom_transparent.png"
            />
            {/* HERO */}
            <section
                data-testid="hero"
                className="relative min-h-screen flex flex-col"
            >
                <div className="container-vyom flex-1 flex flex-col justify-end pt-32 pb-12 md:pb-20">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end">
                        <div className="md:col-span-7">
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, ease }}
                                className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-8"
                            >
                                Est. India · Trusted across the world
                            </motion.div>
                            <h1 className="font-serif-display text-[44px] sm:text-6xl lg:text-7xl xl:text-[104px] leading-[0.98] tracking-tight text-vyom-ink">
                                <motion.span
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 1.2,
                                        ease,
                                        delay: 0.05,
                                    }}
                                    className="block"
                                >
                                    Linen, that
                                </motion.span>
                                <motion.span
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 1.2,
                                        ease,
                                        delay: 0.2,
                                    }}
                                    className="block italic font-normal"
                                >
                                    speaks softly,
                                </motion.span>
                                <motion.span
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 1.2,
                                        ease,
                                        delay: 0.35,
                                    }}
                                    className="block"
                                >
                                    and stays.
                                </motion.span>
                            </h1>
                        </div>

                        <div className="md:col-span-4 md:col-start-9 md:pb-4">
                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 1,
                                    ease,
                                    delay: 0.55,
                                }}
                                className="text-[19px] leading-relaxed text-vyom-mute max-w-sm"
                            >
                                Premium bed and bath linen, woven in India for
                                houses that hold themselves to a quieter
                                standard.
                            </motion.p>
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease, delay: 0.7 }}
                                className="mt-8 flex items-center gap-6"
                            >
                                <Link
                                    to="/collections"
                                    data-testid="hero-cta-collections"
                                    className="text-[19px] whitespace-nowrap uppercase tracking-overline border border-vyom-ink px-6 py-3 text-vyom-ink hover:bg-vyom-ink hover:text-vyom-bg transition-colors duration-500"
                                >
                                    The Collections
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.6, ease, delay: 0.4 }}
                    className="container-vyom"
                >
                    <div className="relative img-veil overflow-hidden h-[58vh] md:h-[72vh]">
                        <motion.img
                            initial={{ scale: 1.08 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 2.2, ease }}
                            src={IMAGES.hero_alt}
                            alt="Folded linen bedding on a softly lit bed"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </div>
                </motion.div>
            </section>

            {/* MARQUEE / MARKETS */}
            <section
                data-testid="markets-strip"
                className="border-y border-vyom-rule"
            >
                <div className="container-vyom py-10 md:py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute">
                        Trusted by distinguished houses
                    </div>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 font-serif-display text-2xl md:text-3xl text-vyom-ink">
                        <span className="text-[17.5px] uppercase tracking-overline text-vyom-mute font-sans-ui not-italic">
                            Across
                        </span>
                        <span className="italic">the</span>
                        <span>world.</span>
                    </div>
                </div>
            </section>

            {/* ABOUT — editorial split */}
            <section
                data-testid="about-section"
                className="section-pad container-vyom"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-5 md:sticky md:top-32 md:self-start">
                        <Reveal>
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-6">
                                I — On craft
                            </div>
                            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                Elevated experiences,{" "}
                                <span className="italic">woven</span> with
                                precision.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-6 md:col-start-7">
                        <Reveal delay={0.1}>
                            <p className="text-[21px] md:text-[22.5px] leading-[1.7] text-vyom-ink/90 max-w-xl">
                                Vyom blends India's textile heritage with
                                global precision. Our collections are trusted
                                by leading brands across the world — built on
                                boutique agility, refined process, and
                                unwavering consistency.
                            </p>
                            <p className="text-[19px] leading-[1.8] text-vyom-mute mt-8 max-w-xl">
                                Every order — from grand volumes to bespoke
                                touches — is delivered with exacting precision.
                                The result is linen that does not announce
                                itself, only holds the room.
                            </p>
                            <div className="mt-12 flex items-center gap-6">
                                <Link
                                    to="/about"
                                    data-testid="about-cta"
                                    className="text-[19px] whitespace-nowrap uppercase tracking-overline text-vyom-ink link-underline"
                                >
                                    The Vyom story
                                </Link>
                                <span className="text-vyom-stone">/</span>
                                <span className="text-[19px] uppercase tracking-overline text-vyom-mute">
                                    Est. United Bleachers · 1949
                                </span>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* COLLECTIONS GRID */}
            <section
                data-testid="collections-section"
                className="border-t border-vyom-rule"
            >
                <div className="container-vyom section-pad">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-24">
                        <div className="md:col-span-5">
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-6">
                                II — Collections
                            </div>
                            <Reveal as="h2">
                                <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                    Choose the{" "}
                                    <span className="italic">finest</span>{" "}
                                    linen.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-5 md:col-start-8 md:pt-4">
                            <Reveal delay={0.1}>
                                <p className="text-[19px] leading-relaxed text-vyom-mute max-w-md">
                                    Six collections, each refined for the
                                    spaces they inhabit — bedrooms, baths, and
                                    tables. All in 100% cotton, made for
                                    decades of use.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-20 md:gap-y-28">
                        {COLLECTIONS.map((c, i) => (
                            <Reveal
                                key={c.slug}
                                delay={(i % 3) * 0.08}
                                as="article"
                            >
                                <article
                                    data-testid={`collection-card-${c.slug}`}
                                    className="group cursor-default"
                                >
                                    <div className="overflow-hidden aspect-[4/5] bg-vyom-bg2">
                                        <img
                                            src={c.image}
                                            alt={c.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="mt-6 flex items-baseline justify-between gap-4">
                                        <h3 className="font-serif-display text-2xl md:text-[26px] leading-tight">
                                            {c.title}
                                        </h3>
                                        <span className="text-[17.5px] uppercase tracking-overline text-vyom-mute">
                                            0{i + 1}
                                        </span>
                                    </div>
                                    <p className="mt-3 text-[17.5px] leading-relaxed text-vyom-mute max-w-xs">
                                        {c.copy}
                                    </p>
                                </article>
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-20 flex justify-center">
                        <Link
                            to="/collections"
                            data-testid="collections-cta"
                            className="text-[19px] whitespace-nowrap uppercase tracking-overline border border-vyom-ink px-8 py-3.5 text-vyom-ink hover:bg-vyom-ink hover:text-vyom-bg transition-colors duration-500"
                        >
                            View all collections
                        </Link>
                    </div>
                </div>
            </section>

            {/* OUR PROMISE */}
            <section
                data-testid="promise-section"
                className="border-t border-vyom-rule bg-vyom-bg2/40"
            >
                <div className="container-vyom section-pad grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
                    <div className="md:col-span-6 order-2 md:order-1">
                        <ImageReveal
                            src={IMAGES.heritage}
                            alt="Spools of thread — seventy-five years of craft"
                            className="aspect-[4/5] md:aspect-[3/4]"
                        />
                    </div>
                    <div className="md:col-span-5 md:col-start-8 order-1 md:order-2">
                        <Reveal>
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-6">
                                III — The promise
                            </div>
                            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                Our promise.{" "}
                                <span className="italic">Our pride.</span>
                            </h2>
                            <p className="mt-8 text-[20px] leading-[1.8] text-vyom-ink/85 max-w-md">
                                Excellence is not an aspiration — it is the
                                essence of every thread we weave.
                            </p>
                            <p className="mt-6 text-[19px] leading-[1.8] text-vyom-mute max-w-md">
                                Quality, transparency, and promptness shape
                                every partnership. We unite India's textile
                                artistry with global benchmarks so that
                                distinguished brands may build spaces of quiet
                                luxury.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* WHY VYOM */}
            <section
                data-testid="why-vyom"
                className="border-t border-vyom-rule"
            >
                <div className="container-vyom section-pad">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-20">
                        <div className="md:col-span-6">
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-6">
                                IV — Why Vyom
                            </div>
                            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                Six measures of{" "}
                                <span className="italic">distinction.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                        {CAPABILITIES.map((c, i) => (
                            <Reveal
                                key={c.index}
                                delay={(i % 3) * 0.08}
                                as="div"
                            >
                                <div
                                    data-testid={`capability-${c.index}`}
                                    className={`p-8 md:p-10 lg:p-12 border-vyom-rule ${
                                        i % 3 !== 2
                                            ? "lg:border-r"
                                            : ""
                                    } ${
                                        i % 2 === 0
                                            ? "md:border-r lg:border-r"
                                            : ""
                                    } ${
                                        i % 2 !== 0 && i % 3 === 2
                                            ? "md:border-r-0 lg:border-r-0"
                                            : ""
                                    } border-t`}
                                >
                                    <div className="font-serif-display italic text-vyom-mute text-[22px] mb-8">
                                        — {c.index}
                                    </div>
                                    <h3 className="font-serif-display text-[26px] md:text-[28px] leading-tight tracking-tight">
                                        {c.title}
                                    </h3>
                                    <p className="mt-5 text-[22.5px] leading-relaxed text-vyom-mute max-w-xs">
                                        {c.copy}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA EDITORIAL */}
            <section
                data-testid="closing-cta"
                className="border-t border-vyom-rule"
            >
                <div className="container-vyom section-pad">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-end">
                        <div className="md:col-span-7">
                            <Reveal>
                                <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-6">
                                    V — Together
                                </div>
                                <h2 className="font-serif-display text-5xl md:text-7xl lg:text-[96px] leading-[0.98] tracking-tight">
                                    Let us create{" "}
                                    <span className="italic">
                                        something
                                    </span>{" "}
                                    exceptional, together.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-4 md:col-start-9">
                            <Reveal delay={0.15}>
                                <p className="text-[19px] leading-relaxed text-vyom-mute max-w-sm">
                                    Custom programs for retail, hospitality,
                                    and direct-to-consumer brands. Begin with
                                    a sample — proceed at your pace.
                                </p>
                                <div className="mt-10">
                                    <Link
                                        to="/contact"
                                        data-testid="closing-cta-button"
                                        className="text-[19px] whitespace-nowrap uppercase tracking-overline border border-vyom-ink px-8 py-3.5 text-vyom-ink hover:bg-vyom-ink hover:text-vyom-bg transition-colors duration-500"
                                    >
                                        Begin a conversation
                                    </Link>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* HERITAGE STRIP */}
            <section
                data-testid="heritage-strip"
                className="border-t border-vyom-rule bg-vyom-ink text-vyom-bg"
            >
                <div className="container-vyom py-20 md:py-28">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
                        <div className="md:col-span-5">
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-stone mb-6">
                                Your brand · Our craft
                            </div>
                            <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                                From the house of{" "}
                                <span className="italic">
                                    United Bleachers.
                                </span>
                            </h3>
                        </div>
                        <div className="md:col-span-6 md:col-start-7">
                            <p className="text-[19.5px] leading-[1.8] text-vyom-bg/80 max-w-lg">
                                Vyom Tex Linen is powered by United Bleachers
                                — a textile legacy with seventy-five years of
                                crafting quality fabrics that travel the world.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
